// A TELA DE INTERVALO: o jogo para no fim de cada quarto e o técnico decide.
//
// É o momento em que um treinador de verdade age — vê quem está em falta,
// quem está frio, e mexe. Sem essa parada, a partida ao vivo era um vídeo:
// dava para pausar, mas nada convidava a isso, e quase ninguém mexia.
//
// Quem prefere ver o jogo correr desliga a parada num toque, e a escolha fica
// guardada. Obrigar três paradas por jogo em quem só quer o placar seria
// trocar um problema por outro.

import React from 'react';
import { TACTICS } from '../engine/tuning.js';
import { Ovr } from './ui.jsx';

const PARADA_KEY = 'basquete82.pararNosQuartos';

export function pararNosQuartos() {
  try { return localStorage.getItem(PARADA_KEY) !== 'nao'; } catch { return true; }
}

export function setPararNosQuartos(ligado) {
  try { localStorage.setItem(PARADA_KEY, ligado ? 'sim' : 'nao'); } catch { /* sem drama */ }
}

const NOME_QUARTO = (q) => (q <= 4 ? `${q}º quarto` : `Prorrogação ${q - 4}`);

/**
 * @param quarto      o quarto que ACABOU
 * @param placar      { nos, eles, nomeNos, nomeEles }
 * @param noQuarto    { nos, eles } — pontos só deste quarto
 * @param court       quem está em quadra (o onCourt de courtState)
 * @param banco       quem está disponível fora
 * @param tactics     a tática atual do time do usuário
 */
export function Intervalo({
  quarto, placar, noQuarto, court, banco, tactics,
  onTactics, onSubstituir, onContinuar, onDesligar,
}) {
  const dif = placar.nos - placar.eles;
  const situacao = dif > 0 ? 'à frente' : dif < 0 ? 'atrás' : 'empatado';

  // Quem está em risco: 4 faltas num jogo de 5 é a conta que o técnico faz.
  const risco = court.filter((p) => (p.fouls ?? 0) >= 4);
  // Quem está carregando o time neste jogo.
  const destaque = [...court].sort((a, b) => (b.pts ?? 0) - (a.pts ?? 0))[0];

  return (
    <div className="wrap">
      <div className="card intervalo-topo">
        <p className="kicker">• Intervalo</p>
        <h1 style={{ margin: '2px 0 6px' }}>
          Fim do {NOME_QUARTO(quarto)}
        </h1>
        <div className="intervalo-placar">
          <div className={`lado ${dif > 0 ? 'lead' : ''}`}>
            <div className="nm">{placar.nomeNos}</div>
            <div className="pt tabular">{placar.nos}</div>
          </div>
          <div className="x">×</div>
          <div className={`lado ${dif < 0 ? 'lead' : ''}`}>
            <div className="nm">{placar.nomeEles}</div>
            <div className="pt tabular">{placar.eles}</div>
          </div>
        </div>
        <div className="small dim center">
          {situacao === 'empatado'
            ? 'Empatado'
            : `${Math.abs(dif)} ${Math.abs(dif) === 1 ? 'ponto' : 'pontos'} ${situacao}`}
          {' · '}
          neste quarto {noQuarto.nos} a {noQuarto.eles}
        </div>
      </div>

      <button className="primary" style={{ width: '100%', padding: 14, margin: '10px 0 4px' }}
        onClick={onContinuar}>
        {quarto >= 4 ? 'jogar a prorrogação' : `jogar o ${quarto + 1}º quarto`}
      </button>

      {(risco.length > 0 || destaque) && (
        <div className="card tight">
          {destaque && (destaque.pts ?? 0) > 0 && (
            <div className="small">
              <b>{destaque.name}</b> é quem está carregando:{' '}
              {destaque.pts} pts, {destaque.reb} reb, {destaque.ast} ast.
            </div>
          )}
          {risco.map((p) => (
            <div key={p.id} className="small warn" style={{ marginTop: 4 }}>
              ⚠️ {p.name} está com {p.fouls} faltas
              {quarto < 4 && ' — pense em poupar'}.
            </div>
          ))}
        </div>
      )}

      <h2>Em quadra</h2>
      <div className="card">
        {court.map((p) => (
          <div key={p.id} className="row spread intervalo-linha">
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{p.name} <Ovr value={p.overall} /></div>
              <div className="small dim">
                {p.pts} pts · {p.reb} reb · {p.ast} ast ·{' '}
                <span className={(p.fouls ?? 0) >= 4 ? 'warn' : ''}>{p.fouls} faltas</span>
              </div>
            </div>
          </div>
        ))}
        <button className="ghost" style={{ width: '100%', marginTop: 10 }}
          onClick={onSubstituir} disabled={!banco.length}>
          {banco.length ? 'trocar jogadores' : 'sem ninguém no banco'}
        </button>
      </div>

      <h2>Ajustar a tática</h2>
      <div className="card">
        <div className="small dim" style={{ marginBottom: 10 }}>
          Vale do próximo quarto em diante — o que já aconteceu não muda.
        </div>
        {Object.entries(TACTICS).map(([grupo, opcoes]) => (
          <div key={grupo} style={{ marginBottom: 12 }}>
            <div className="small dim" style={{ marginBottom: 5, textTransform: 'capitalize' }}>
              {grupo === 'pace' ? 'Ritmo' : grupo === 'offense' ? 'Ataque'
                : grupo === 'defense' ? 'Defesa' : 'Foco'}
            </div>
            <div className="row wrap" style={{ gap: 6 }}>
              {Object.entries(opcoes).map(([id, o]) => (
                <button key={id} title={o.hint}
                  className={`sm ${tactics[grupo] === id ? 'primary' : 'ghost'}`}
                  onClick={() => onTactics({ ...tactics, [grupo]: id })}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="center" style={{ margin: '14px 0 30px' }}>
        <button className="ghost sm" onClick={onDesligar}>
          não parar mais nos intervalos
        </button>
        <div className="small dim" style={{ marginTop: 6 }}>
          Dá para religar durante o jogo, no botão “intervalos”.
        </div>
      </div>
    </div>
  );
}
